import { Link } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/shared/status-badge";
import { ProgressBar } from "@/components/shared/progress-bar";
import { getKrTypeLabel, getQuarterLabel } from "@/lib/status-machine";
import { formatDueDate } from "@/lib/date";
import type { KeyResult } from "@/types/entities";

interface KeyResultCardProps {
  keyResult: KeyResult;
  year?: number;
}

export function KeyResultCard({ keyResult, year }: KeyResultCardProps) {
  return (
    <Link to="/key-results/$id" params={{ id: keyResult.id }} className="block">
      <Card className="transition-colors hover:border-[hsl(var(--primary))]">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-sm font-medium">{keyResult.title}</CardTitle>
            <StatusBadge status={keyResult.status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <ProgressBar value={keyResult.completion_percent} className="flex-1" />
            <span className="text-sm font-medium tabular-nums">
              {Math.round(keyResult.completion_percent)}%
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">{getQuarterLabel(keyResult.quarter, year)}</Badge>
            <Badge variant="secondary">{getKrTypeLabel(keyResult.type)}</Badge>
            {keyResult.type === "metric" && keyResult.target_value != null && (
              <Badge variant="secondary">
                {keyResult.current_value ?? 0} / {keyResult.target_value}
              </Badge>
            )}
          </div>
          {keyResult.due_date && (
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              Due: {formatDueDate(keyResult.due_date)}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
